import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaRegStar, FaClock, FaRoute } from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/Profile.scss";
import { useLanguage } from "../context/LanguageContext";
import useAuthToken from "../hooks/useAuthToken";
import {
  getProfile,
  updateProfile,
  changePassword,
} from "../api/auth";
import {
  getBookings,
  checkAvailability,
  rescheduleBooking,
  cancelBooking,
} from "../api/bookings";
import { issueBookingActionToken } from "../api/bookingActions";
import { getRewardStatus, redeemRewardVoucher } from "../api/rewards";
import { getLocations } from "../api/locations";
import { listReceipts, downloadReceipt } from "../api/receipts";
import ProfileHero from "../components/profile/ProfileHero";
import {
  UpcomingBookingsCard,
  PastRentalsCard,
  ReceiptsCard,
  QuickActionsCard,
} from "../components/profile/ProfileCards";
import {
  EditProfileModal,
  PasswordModal,
  CalendarModal,
  RescheduleModal,
  CancelModal,
  BookingQrModal,
} from "../components/profile/ProfileModals";

const formatIsoDate = (year, monthIndex, day) => {
  const month = String(monthIndex + 1).padStart(2, "0");
  const dayString = String(day).padStart(2, "0");
  return `${year}-${month}-${dayString}`;
};

const formatDisplayDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTimePart = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const buildCalendarGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7; // shift so Monday is first column
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, index) => {
    const dayNumber = index - offset + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) {
      return null;
    }
    const isoDate = formatIsoDate(year, month, dayNumber);
    return { dayNumber, isoDate };
  });
};

const normalizeRewardVoucher = (voucher) =>
  voucher
    ? {
        code: voucher.code,
        issuedUtc: voucher.issuedUtc,
        redeemedUtc: voucher.redeemedUtc ?? null,
        redeemed: Boolean(voucher.redeemedUtc),
      }
    : null;

const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;
const PASSWORD_MIN_LENGTH = 9;
const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{9,}$/;

const DEFAULT_BOOKING_DURATION_MINUTES = 90;
const DEFAULT_BOOKING_DURATION_MS = DEFAULT_BOOKING_DURATION_MINUTES * 60 * 1000;
const TECHNICAL_ERROR_PATTERN = /\b(?:Exception|StackTrace|System\.|Microsoft\.)\b|(?:\bat\s+[A-Za-z_$.][\w$.<>]*\()|(?:\bin\s+\S+:\d+)/i;

const resolveSafeErrorMessage = (error, fallbackMessage) => {
  const raw = typeof error?.message === "string" ? error.message.trim() : "";
  if (!raw) return fallbackMessage;
  if (raw.length > 220) return fallbackMessage;
  if (TECHNICAL_ERROR_PATTERN.test(raw)) return fallbackMessage;
  return raw;
};

const EQUIPMENT_TYPE_LABELS = {
  "4a1d4f5e-b3c7-4d3f-8a4c-54f5cc3d2c8e": "Solo Glide",
  "53dbef07-e0d7-40f1-95d6-4a4a7a3f2e2b": "Party Board",
  "e9d23ea4-5f3c-4f1b-bb2b-9adf5b1f4d7c": "Glowing SUP",
  "9f03a1a6-8e3c-4f79-9f24-4c1c7eb2fd55": "Solo Kayak",
  "b4c5d9f0-7b0e-4d5e-ae9a-13a8c3f8409e": "Big Kayak",
  "c8d96baf-5fd8-4a29-8b5f-0a8d8f0e2a6f": "Utilities",

};

const normalizeGuid = (value) => (typeof value === "string" ? value.toLowerCase() : value);

const resolveLocationName = (locationId, locationsById, fallback) => {
  const normalized = normalizeGuid(locationId);
  const match = normalized ? locationsById.get(normalized) : null;
  const name = typeof match?.name === "string" ? match.name.trim() : "";
  if (name) return name;
  if (typeof fallback === "string" && fallback.trim()) return fallback.trim();
  return "Wayli Base";
};

const buildLocationMapUrl = (location) => {
  if (!location) return "";
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }

  const query = [location.name, location.address, location.city, location.country]
    .filter((part) => typeof part === "string" && part.trim())
    .join(", ");
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
};

const normalizePhoneInput = (value) => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "";
  let normalized = trimmed.replace(/[^\d+]/g, "");
  if (normalized.startsWith("00")) {
    normalized = `+${normalized.slice(2)}`;
  }
  if (normalized.includes("+") && !normalized.startsWith("+")) {
    normalized = normalized.replaceAll("+", "");
    normalized = `+${normalized}`;
  }
  return normalized;
};

const describeEquipment = (items, fallbackType) => {
  if (!Array.isArray(items) || items.length === 0) {
    return fallbackType ?? "Booking";
  }

  return items
    .map((item) => {
      const label =
        EQUIPMENT_TYPE_LABELS[normalizeGuid(item.equipmentTypeId)] ??
        "Equipment";
      return `${item.quantity}× ${label}`;
    })
    .join(", ");
};

const buildDurationLabel = (startUtc, endUtc) => {
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "";
  }
  const diffMs = Math.max(0, end.getTime() - start.getTime());
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.round((diffMs % 3_600_000) / 60_000);
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  return parts.join(" ") || "—";
};

const isRentalLikeEventTitle = (eventTitle) => {
  const normalized = String(eventTitle ?? "").trim().toLowerCase();
  return (
    normalized.includes("after work") ||
    normalized.includes("after-work") ||
    normalized.includes("evening swim") ||
    normalized.includes("evening booking")
  );
};

const mapBookingResponseToView = (booking) => {
  const start = new Date(booking.startUtc);
  const end = new Date(booking.endUtc);
  const hasValidStart = !Number.isNaN(start.getTime());
  const hasValidEnd = !Number.isNaN(end.getTime());
  const status = booking.status ?? "Pending";
  const sessionType = booking.type ?? "Day";
  const isEventBooking = String(sessionType).toLowerCase() === "event";
  const eventTitle = booking.eventTitle ?? "";
  const startMs = hasValidStart ? start.getTime() : null;
  const endMs = hasValidEnd ? end.getTime() : null;
  const durationMs =
    startMs !== null && endMs !== null && endMs > startMs
      ? endMs - startMs
      : DEFAULT_BOOKING_DURATION_MS;
  const equipmentLabel = describeEquipment(booking.items, sessionType);
  return {
    id: booking.id,
    type: equipmentLabel,
    sessionType,
    isEventBooking,
    isGuidedEventBooking: isEventBooking && !isRentalLikeEventTitle(eventTitle),
    eventTitle,
    status,
    locationId: booking.locationId,
    location: "Wayli Base",
    locationMapUrl: "",
    date: hasValidStart
      ? formatIsoDate(start.getFullYear(), start.getMonth(), start.getDate())
      : "",
    displayDate: hasValidStart ? formatDisplayDate(start) : "",
    time: hasValidStart ? formatTimePart(start) : "",
    duration: buildDurationLabel(start, end),
    items: booking.items ?? [],
    startUtc: booking.startUtc,
    endUtc: booking.endUtc,
    durationMs,
  };
};

const normalizeBookingsPayload = (payload) =>
  Array.isArray(payload) ? payload.map(mapBookingResponseToView) : [];

const isCancelledStatus = (booking) =>
  String(booking.status ?? "").toLowerCase().includes("cancel");

const isCompletedStatus = (booking) =>
  String(booking.status ?? "").toLowerCase().includes("complete");

const isActiveStatus = (booking) =>
  ["confirmed", "inuse"].includes(String(booking.status ?? "").toLowerCase());

const isPastBooking = (booking) => {
  if (isCompletedStatus(booking)) return true;
  if (!booking?.endUtc) return false;
  const endDate = new Date(booking.endUtc);
  return !Number.isNaN(endDate.getTime()) && endDate.getTime() <= Date.now();
};

const isPastEligibleStatus = (booking) =>
  isActiveStatus(booking) || isCompletedStatus(booking);

const ProfilePage = () => {
  const { language, t } = useLanguage();
  const daysOfWeek = useMemo(
    () => [
      t("profilePage.sections.upcoming.calendar.days.mon", "Mon"),
      t("profilePage.sections.upcoming.calendar.days.tue", "Tue"),
      t("profilePage.sections.upcoming.calendar.days.wed", "Wed"),
      t("profilePage.sections.upcoming.calendar.days.thu", "Thu"),
      t("profilePage.sections.upcoming.calendar.days.fri", "Fri"),
      t("profilePage.sections.upcoming.calendar.days.sat", "Sat"),
      t("profilePage.sections.upcoming.calendar.days.sun", "Sun"),
    ],
    [t]
  );
  const { isAuthenticated, logout } = useAuthToken();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState("");
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [locations, setLocations] = useState([]);
  const [qrModal, setQrModal] = useState({
    isOpen: false,
    booking: null,
    actionUrl: "",
    qrCodeDataUrl: "",
    expiresUtc: null,
    isLoading: false,
    error: "",
  });
  const loadBookings = useCallback(
    async (withSpinner = true) => {
      if (!isAuthenticated) {
        setBookings([]);
        setBookingsError("");
        setBookingsLoading(false);
        return;
      }

      if (withSpinner) {
        setBookingsLoading(true);
      }
      setBookingsError("");

      try {
        const payload = await getBookings();
        setBookings(normalizeBookingsPayload(payload));
      } catch (err) {
        setBookings([]);
        setBookingsError(
          err?.message ??
          t(
            "profilePage.sections.upcoming.error",
            "We couldn’t load your bookings. Please try again shortly."
          )
        );
      } finally {
        setBookingsLoading(false);
      }
    },
    [isAuthenticated, t]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setFormData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const load = async () => {
      try {
        const data = await getProfile();
        if (!cancelled) {
          setProfile(data);
          setFormData({ ...data });
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(
            t("profilePage.error.loadFailed", "Could not load your profile. Please log in again.")
          );
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, t]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const loadReceipts = useCallback(
    async (withSpinner = true) => {
      if (!isAuthenticated) {
        setReceipts([]);
        setReceiptsError("");
        setReceiptsLoading(false);
        return;
      }

      if (withSpinner) {
        setReceiptsLoading(true);
      }
      setReceiptsError("");

      try {
        const payload = await listReceipts();
        setReceipts(Array.isArray(payload) ? payload : []);
      } catch (err) {
        setReceipts([]);
        setReceiptsError(
          err?.message ??
          t(
            "profilePage.sections.receipts.error",
            "We couldn’t load your receipts. Please try again shortly."
          )
        );
      } finally {
        setReceiptsLoading(false);
      }
    },
    [isAuthenticated, t]
  );

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLocations([]);
      return;
    }

    let cancelled = false;
    getLocations()
      .then((payload) => {
        if (cancelled) return;
        setLocations(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (!cancelled) setLocations([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const locationsById = useMemo(() => {
    const map = new Map();
    for (const location of locations ?? []) {
      if (!location?.id) continue;
      map.set(normalizeGuid(location.id), location);
    }
    return map;
  }, [locations]);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [modalState, setModalState] = useState({ type: null, context: null });
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordStatus, setPasswordStatus] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [rewardVoucher, setRewardVoucher] = useState(null);
  const [rewardProgress, setRewardProgress] = useState(0);
  const [rewardGoal, setRewardGoal] = useState(5);
  const [rewardCanRedeem, setRewardCanRedeem] = useState(false);
  const [loyaltyStateLoaded, setLoyaltyStateLoaded] = useState(false);
  const [loyaltyVoucher, setLoyaltyVoucher] = useState(null);

  const bookingsWithLocationNames = useMemo(() => {
    return (bookings ?? []).map((booking) => {
      const location = booking.locationId
        ? locationsById.get(normalizeGuid(booking.locationId)) ?? null
        : null;
      return {
        ...booking,
        location: resolveLocationName(booking.locationId, locationsById, booking.location),
        locationMapUrl: buildLocationMapUrl(location),
      };
    });
  }, [bookings, locationsById]);

  const upcomingBookings = useMemo(
    () =>
      bookingsWithLocationNames.filter(
        (booking) => !isCancelledStatus(booking) && !isPastBooking(booking)
      ),
    [bookingsWithLocationNames]
  );

  const pastBookings = useMemo(
    () =>
      bookingsWithLocationNames.filter(
        (booking) => isPastEligibleStatus(booking) && isPastBooking(booking)
      ),
    [bookingsWithLocationNames]
  );

  const receiptsForDisplay = useMemo(() => {
    if (receiptsLoading || receiptsError) {
      return receipts;
    }

    if (Array.isArray(receipts) && receipts.length > 0) {
      return receipts;
    }

    return (pastBookings ?? []).map((booking) => ({
      bookingId: booking.id,
      startUtc: booking.startUtc,
      endUtc: booking.endUtc,
      locationName: booking.location,
    }));
  }, [receipts, receiptsLoading, receiptsError, pastBookings]);

  useEffect(() => {
    if (!isAuthenticated || !profile) {
      setRewardProgress(0);
      setRewardGoal(5);
      setRewardCanRedeem(false);
      setLoyaltyVoucher(null);
      setLoyaltyStateLoaded(false);
      return;
    }

    let cancelled = false;

    const loadRewardStatus = async () => {
      try {
        const status = await getRewardStatus();
        if (cancelled) return;
        setRewardProgress(Math.max(0, status?.progressCount ?? 0));
        setRewardGoal(Math.max(1, status?.ridesGoal ?? 5));
        setRewardCanRedeem(Boolean(status?.canRedeem));
        setLoyaltyVoucher(normalizeRewardVoucher(status?.activeVoucher ?? null));
      } catch {
        if (!cancelled) {
          setRewardProgress(0);
          setRewardGoal(5);
          setRewardCanRedeem(false);
          setLoyaltyVoucher(null);
        }
      } finally {
        if (!cancelled) {
          setLoyaltyStateLoaded(true);
        }
      }
    };

    loadRewardStatus();

    return () => {
      cancelled = true;
    };
  }, [profile, isAuthenticated]);

  useEffect(() => {
    if (loyaltyVoucher && !loyaltyVoucher.redeemed) {
      setRewardVoucher(loyaltyVoucher.code);
    } else {
      setRewardVoucher(null);
    }
  }, [loyaltyVoucher]);

  useEffect(() => {
    if (!upcomingBookings.length) {
      setCalendarSelectedDate(null);
      return;
    }
    const firstDatedBooking = upcomingBookings.find((booking) => booking.date);
    setCalendarSelectedDate((prev) => {
      if (prev && upcomingBookings.some((booking) => booking.date === prev)) {
        return prev;
      }
      if (firstDatedBooking) {
        return firstDatedBooking.date;
      }
      return upcomingBookings[0]?.date ?? null;
    });
  }, [upcomingBookings]);

  const sortedBookings = useMemo(() => {
    const toTimestamp = (entry) => {
      const candidate = new Date(`${entry.date ?? ""}T${entry.time ?? "00:00"}`);
      return candidate.getTime();
    };
    return [...upcomingBookings].sort((a, b) => {
      const aTime = toTimestamp(a);
      const bTime = toTimestamp(b);
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return aTime - bTime;
    });
  }, [upcomingBookings]);

  const todayIso = useMemo(() => {
    const now = new Date();
    return formatIsoDate(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const completedCount = pastBookings.length;
  const upcomingCount = upcomingBookings.length;
  const ridesGoal = Math.max(1, rewardGoal || 5);
  const progressCount = Math.max(0, rewardProgress);
  const cappedRidesTowardReward = Math.min(ridesGoal, progressCount);
  const ridesCompleted = cappedRidesTowardReward;
  const ridesRemaining = Math.max(0, ridesGoal - cappedRidesTowardReward);
  const progressFraction = Math.min(1, cappedRidesTowardReward / ridesGoal);
  const progressPercent = Math.round(progressFraction * 100);
  const canRedeemReward = loyaltyStateLoaded && rewardCanRedeem;
  const loyaltyDescription = canRedeemReward
    ? t(
      "profilePage.hero.loyalty.ready",
      "You’ve completed all {goal} paddles — redeem your €20 discount!",
      { goal: ridesGoal }
    )
    : t(
      "profilePage.hero.loyalty.description",
      "{completed} of {goal} paddles completed — {remaining} more until your €20 discount!",
      { completed: ridesCompleted, goal: ridesGoal, remaining: ridesRemaining }
    );

  const metrics = [
    { icon: FaClock, label: t("profilePage.hero.metrics.upcoming", "Upcoming paddles"), value: upcomingCount },
    { icon: FaRoute, label: t("profilePage.hero.metrics.completed", "Completed rides"), value: completedCount },
    { icon: FaRegStar, label: t("profilePage.hero.metrics.loyalty", "Loyalty progress"), value: `${progressPercent}%` },
  ];

  const startEditing = () => {
    if (!profile) {
      return;
    }
    closeModal();
    setFormData({ ...profile });
    setIsEditing(true);
    setSaveStatus("");
    setSaveError("");
    setSaveStatus("");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...(prev ?? {}), [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...(prev ?? {}), phone: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!formData || !isAuthenticated) {
      return;
    }

    const trimmedName = (formData.name ?? "").trim();
    if (!trimmedName) {
      setSaveError(t("profilePage.edit.validation.nameRequired", "Name is required."));
      return;
    }
    if (trimmedName.length > 30) {
      setSaveError(
        t("profilePage.edit.validation.nameLength", "Name must be 30 characters or fewer.")
      );
      return;
    }

    const normalizedPhone = normalizePhoneInput(formData.phone);
    if (normalizedPhone && !PHONE_PATTERN.test(normalizedPhone)) {
      setSaveError(
        t(
          "profilePage.edit.validation.phone",
          "Phone number must be in international format (e.g., +43123456789)."
        )
      );
      return;
    }

    setSaveError("");
    setIsSavingProfile(true);
    try {
      const payload = {
        name: trimmedName,
        email: (formData.email ?? "").trim(),
        phone: normalizedPhone,
      };

      const updated = await updateProfile(payload);
      setProfile(updated);
      setFormData({ ...updated });
      setIsEditing(false);
      setSaveStatus(t("profilePage.status.updated", "Profile updated successfully."));
      setTimeout(() => setSaveStatus(""), 2600);
    } catch (err) {
      setSaveError(
        err?.message ?? t("profilePage.status.updateError", "Could not save profile changes.")
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData(profile ? { ...profile } : null);
    setSaveStatus("");
    setSaveError("");
    setIsSavingProfile(false);
  };

  const closeModal = () => {
    setModalState({ type: null, context: null });
    setRescheduleError("");
    setCancelError("");
    setIsRescheduling(false);
    setIsCancelling(false);
  };

  const openCalendar = () => {
    if (calendarSelectedDate) {
      const selectedDate = new Date(calendarSelectedDate);
      setCalendarMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    } else if (sortedBookings.length) {
      const firstBooking = sortedBookings[0];
      const firstDate = new Date(firstBooking.date || Date.now());
      if (!Number.isNaN(firstDate.getTime())) {
        setCalendarMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
      }
      setCalendarSelectedDate(firstBooking.date || null);
    } else {
      setCalendarSelectedDate(null);
    }
    setModalState({ type: "calendar", context: null });
  };

  const openReschedule = (booking) => {
    setRescheduleError("");
    setIsRescheduling(false);
    setModalState({ type: "reschedule", context: booking });
  };

  const openCancel = (booking) => {
    setCancelError("");
    setIsCancelling(false);
    setModalState({ type: "cancel", context: booking });
  };

  const closeQrModal = () => {
    setQrModal({
      isOpen: false,
      booking: null,
      actionUrl: "",
      qrCodeDataUrl: "",
      expiresUtc: null,
      isLoading: false,
      error: "",
    });
  };

  const openQrModal = async (booking) => {
    if (!booking?.id) return;
    if (!isAuthenticated) {
      setQrModal((prev) => ({
        ...prev,
        isOpen: true,
        booking,
        error: t("profilePage.sections.upcoming.authRequired", "Please log in again."),
      }));
      return;
    }

    setQrModal({
      isOpen: true,
      booking,
      actionUrl: "",
      qrCodeDataUrl: "",
      expiresUtc: null,
      isLoading: true,
      error: "",
    });

    try {
      const payload = await issueBookingActionToken(booking.id);
      setQrModal({
        isOpen: true,
        booking,
        actionUrl: payload.actionUrl,
        qrCodeDataUrl: payload.qrCodeDataUrl,
        expiresUtc: payload.expiresUtc,
        isLoading: false,
        error: "",
      });
    } catch (err) {
      setQrModal({
        isOpen: true,
        booking,
        actionUrl: "",
        qrCodeDataUrl: "",
        expiresUtc: null,
        isLoading: false,
        error: err?.message ?? t("profilePage.sections.upcoming.qrError", "Unable to generate a QR code."),
      });
    }
  };

  const handleRescheduleSave = async ({ startDate, endDate }) => {
    const booking = modalState.context;
    if (!booking) {
      closeModal();
      return;
    }
    if (
      !(startDate instanceof Date) ||
      Number.isNaN(startDate.getTime()) ||
      !(endDate instanceof Date) ||
      Number.isNaN(endDate.getTime()) ||
      endDate.getTime() <= startDate.getTime()
    ) {
      setRescheduleError(
        t("profilePage.sections.upcoming.rescheduleInvalid", "Pick a valid rental range.")
      );
      return;
    }
    if (!isAuthenticated) {
      setRescheduleError(
        t("profilePage.sections.upcoming.authRequired", "Please log in again to manage bookings.")
      );
      return;
    }

    const payload = {
      startUtc: startDate.toISOString(),
      endUtc: endDate.toISOString(),
    };

    setIsRescheduling(true);
    setRescheduleError("");
    try {
      const bookingItemsPayload = Array.isArray(booking.items)
        ? booking.items
          .filter((item) => item?.equipmentTypeId && Number(item.quantity) > 0)
          .map((item) => ({
            equipmentTypeId: item.equipmentTypeId,
            quantity: Number(item.quantity),
          }))
        : [];

      if (booking.locationId && bookingItemsPayload.length) {
        const availability = await checkAvailability({
          locationId: booking.locationId,
          startUtc: payload.startUtc,
          endUtc: payload.endUtc,
          items: bookingItemsPayload,
        });

        if (!availability?.isAvailable) {
          setRescheduleError(
            t(
              "profilePage.sections.upcoming.rescheduleUnavailable",
              "That slot isn’t available for your selected gear. Please pick another time."
            )
          );
          return;
        }
      }

      await rescheduleBooking(booking.id, payload);
      await loadBookings(false);
      setCalendarSelectedDate(
        formatIsoDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
      );
      closeModal();
    } catch (err) {
      setRescheduleError(
        resolveSafeErrorMessage(
          err,
          t(
            "profilePage.sections.upcoming.rescheduleError",
            "We couldn’t reschedule this booking. Please try another slot."
          )
        )
      );
    } finally {
      setIsRescheduling(false);
    }
  };

  const openPasswordModal = () => {
    setPasswordForm({ current: "", next: "", confirm: "" });
    setPasswordError("");
    setPasswordVisibility({ current: false, next: false, confirm: false });
    setIsSavingPassword(false);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordInputChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordError) {
      setPasswordError("");
    }
  };

  const handlePasswordCancel = () => {
    setIsPasswordModalOpen(false);
    setPasswordForm({ current: "", next: "", confirm: "" });
    setPasswordError("");
    setPasswordVisibility({ current: false, next: false, confirm: false });
    setIsSavingPassword(false);
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const trimmedCurrent = passwordForm.current.trim();
    const trimmedNext = passwordForm.next.trim();
    const trimmedConfirm = passwordForm.confirm.trim();

    if (!trimmedCurrent || !trimmedNext || !trimmedConfirm) {
      setPasswordError(
        t("profilePage.password.validation.required", "Fill out every field before saving.")
      );
      return;
    }

    if (trimmedNext.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(
        t(
          "profilePage.password.validation.length",
          "Use at least 9 characters for your new password."
        )
      );
      return;
    }

    if (!PASSWORD_PATTERN.test(trimmedNext)) {
      setPasswordError(
        t(
          "profilePage.password.validation.complexity",
          "Include an uppercase letter, a number, and a special character in your new password."
        )
      );
      return;
    }

    if (trimmedNext !== trimmedConfirm) {
      setPasswordError(
        t("profilePage.password.validation.match", "New password and confirmation must match.")
      );
      return;
    }

    if (trimmedCurrent === trimmedNext) {
      setPasswordError(
        t("profilePage.password.validation.different", "New password must differ from the current one.")
      );
      return;
    }

    if (!isAuthenticated) {
      setPasswordError(t("profilePage.password.validation.auth", "You need to log in again."));
      return;
    }

    setPasswordError("");
    setPasswordStatus("");
    setIsSavingPassword(true);
    try {
      await changePassword({
        currentPassword: trimmedCurrent,
        newPassword: trimmedNext,
      });
      setIsPasswordModalOpen(false);
      setPasswordForm({ current: "", next: "", confirm: "" });
      setPasswordVisibility({ current: false, next: false, confirm: false });
      setPasswordStatus(t("profilePage.password.status.updated", "Password updated successfully."));
      setTimeout(() => setPasswordStatus(""), 2600);
    } catch (err) {
      setPasswordError(
        err?.message ?? t("profilePage.password.status.error", "Could not update your password.")
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleCancelBooking = async () => {
    const booking = modalState.context;
    if (!booking) {
      closeModal();
      return;
    }
    if (!isAuthenticated) {
      setCancelError(
        t("profilePage.sections.upcoming.authRequired", "Please log in again to manage bookings.")
      );
      return;
    }

    setIsCancelling(true);
    setCancelError("");
    try {
      await cancelBooking(booking.id);
      await loadBookings(false);
      closeModal();
    } catch (err) {
      setCancelError(
        err?.message ??
        t(
          "profilePage.sections.upcoming.cancelError",
          "We couldn’t cancel this booking right now. Please try again."
        )
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRedeemReward = async () => {
    if (!canRedeemReward || !loyaltyStateLoaded || !isAuthenticated) {
      return;
    }
    try {
      const voucher = await redeemRewardVoucher();
      setLoyaltyVoucher(normalizeRewardVoucher(voucher));
      setRewardProgress(0);
      setRewardCanRedeem(false);
    } catch {
      // Ignore; fallback refresh below.
    }

    try {
      const status = await getRewardStatus();
      setRewardProgress(Math.max(0, status?.progressCount ?? 0));
      setRewardGoal(Math.max(1, status?.ridesGoal ?? 5));
      setRewardCanRedeem(Boolean(status?.canRedeem));
      setLoyaltyVoucher(normalizeRewardVoucher(status?.activeVoucher ?? null));
    } catch {
      // Ignore; UI will refresh on next load.
    }
  };

  const handleLogout = async () => {
    await logout();
    // Force a direct hop to home so auth guard doesn’t reroute to login mid-logout
    window.location.replace("/");
  };

  const downloadBlob = (blob, fileName, fallback) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      fileName ||
      fallback ||
      `wayli-receipt-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReceipt = async (bookingId) => {
    if (!bookingId || downloadingReceiptId === bookingId) return;
    setDownloadingReceiptId(bookingId);

    try {
      const { blob, fileName } = await downloadReceipt(bookingId);
      downloadBlob(blob, fileName, `wayli-receipt-${bookingId}.pdf`);
      await loadReceipts(false);
    } catch (err) {
      setReceiptsError(
        err?.message ??
        t("profilePage.sections.receipts.downloadFailed", "Failed to download receipt.")
      );
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const calendarGrid = useMemo(() => buildCalendarGrid(calendarMonth), [calendarMonth]);
  const calendarWeeks = useMemo(() => {
    const weeks = [];
    for (let index = 0; index < calendarGrid.length; index += 7) {
      weeks.push(calendarGrid.slice(index, index + 7));
    }
    return weeks;
  }, [calendarGrid]);
  const calendarLabel = useMemo(
    () => calendarMonth.toLocaleDateString(language, { month: "long", year: "numeric" }),
    [calendarMonth, language]
  );

  const bookingsByDate = useMemo(() => {
    return upcomingBookings.reduce((acc, booking) => {
      if (!booking.date) {
        return acc;
      }
      if (!acc[booking.date]) {
        acc[booking.date] = [];
      }
      acc[booking.date].push(booking);
      return acc;
    }, {});
  }, [upcomingBookings]);

  const calendarSelectedBookings = calendarSelectedDate ? bookingsByDate[calendarSelectedDate] ?? [] : [];

  useEffect(() => {
    if (!modalState.type) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalState.type]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsEditing(false);
        setFormData(profile);
        setSaveStatus("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditing, profile]);

  useEffect(() => {
    if (!isPasswordModalOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handlePasswordCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPasswordModalOpen]);

  const goToMonth = (offset) => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  };

  useEffect(() => {
    if (modalState.type !== "calendar" || calendarSelectedDate) {
      return;
    }
    const monthKey = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, "0")}`;
    const monthBooking = sortedBookings.find((booking) => booking.date.startsWith(monthKey));
    if (monthBooking) {
      setCalendarSelectedDate(monthBooking.date);
    }
  }, [calendarMonth, modalState.type, sortedBookings, calendarSelectedDate]);

  const selectedDateHeading = calendarSelectedDate
    ? new Date(calendarSelectedDate).toLocaleDateString(language, {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
    : t("profilePage.sections.upcoming.calendar.prompt", "Pick a day");

  if (loading) {
    return (
      <div className="profile-page">
        <Header />
        <main className="profile-content">
          <p className="profile-status">
            {t("profilePage.loading", "Loading your profile...")}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-page">
        <Header />
        <main className="profile-content">
          <p className="profile-status profile-status--accent">
            {error || t("profilePage.error.missing", "We couldn't retrieve your profile data.")}
          </p>
          <button
            type="button"
            className="profile-btn profile-btn--primary"
            onClick={handleLogout}
          >
            {t("profilePage.error.loginAgain", "Back to login")}
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Header />
      <main className="profile-content">
        <ProfileHero
          profile={profile}
          saveStatus={saveStatus}
          saveError={saveError}
          passwordStatus={passwordStatus}
          metrics={metrics}
          loyaltyDescription={loyaltyDescription}
          progressPercent={progressPercent}
          canRedeemReward={canRedeemReward}
          onRedeemReward={handleRedeemReward}
          rewardVoucher={rewardVoucher}
          t={t}
        />

        <section className="profile-grid">
          <UpcomingBookingsCard
            bookings={sortedBookings}
            isLoading={bookingsLoading}
            error={bookingsError}
            onOpenCalendar={openCalendar}
            onOpenReschedule={openReschedule}
            onOpenCancel={openCancel}
            onOpenQr={openQrModal}
            t={t}
          />
          <PastRentalsCard pastRentals={pastBookings} t={t} />
          <ReceiptsCard
            receipts={receiptsForDisplay}
            isLoading={receiptsLoading}
            error={receiptsError}
            onDownload={handleDownloadReceipt}
            onRefresh={() => loadReceipts(true)}
            downloadingId={downloadingReceiptId}
            t={t}
          />
          <QuickActionsCard
            onEditProfile={startEditing}
            onChangePassword={openPasswordModal}
            onLogout={handleLogout}
            t={t}
          />
        </section>
      </main>
      <Footer />

      <EditProfileModal
        isOpen={isEditing}
        formData={formData ?? profile}
        error={saveError}
        isSaving={isSavingProfile}
        onChange={handleInputChange}
        onPhoneChange={handlePhoneChange}
        onSubmit={handleEditSubmit}
        onCancel={handleCancelEdit}
        t={t}
      />

      <PasswordModal
        isOpen={isPasswordModalOpen}
        passwordForm={passwordForm}
        passwordVisibility={passwordVisibility}
        passwordError={passwordError}
        isSaving={isSavingPassword}
        onInputChange={handlePasswordInputChange}
        onToggleVisibility={togglePasswordVisibility}
        onSubmit={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        t={t}
      />

      <CalendarModal
        isOpen={modalState.type === "calendar"}
        calendarLabel={calendarLabel}
        daysOfWeek={daysOfWeek}
        calendarWeeks={calendarWeeks}
        bookingsByDate={bookingsByDate}
        calendarSelectedDate={calendarSelectedDate}
        calendarSelectedBookings={calendarSelectedBookings}
        selectedDateHeading={selectedDateHeading}
        onClose={closeModal}
        onGoToMonth={goToMonth}
        onSelectDate={setCalendarSelectedDate}
        onOpenReschedule={openReschedule}
        onOpenCancel={openCancel}
        todayIso={todayIso}
        t={t}
      />

      <RescheduleModal
        isOpen={modalState.type === "reschedule"}
        booking={modalState.context}
        location={
          modalState.context?.locationId
            ? locationsById.get(normalizeGuid(modalState.context.locationId)) ?? null
            : null
        }
        onClose={closeModal}
        onSave={handleRescheduleSave}
        isSaving={isRescheduling}
        error={rescheduleError}
        t={t}
      />

      <CancelModal
        isOpen={modalState.type === "cancel"}
        booking={modalState.context}
        onClose={closeModal}
        onConfirm={handleCancelBooking}
        isProcessing={isCancelling}
        error={cancelError}
        t={t}
      />

      <BookingQrModal
        isOpen={qrModal.isOpen}
        booking={qrModal.booking}
        actionUrl={qrModal.actionUrl}
        qrCodeDataUrl={qrModal.qrCodeDataUrl}
        expiresUtc={qrModal.expiresUtc}
        isLoading={qrModal.isLoading}
        error={qrModal.error}
        onClose={closeQrModal}
        t={t}
      />
    </div>
  );
};

export default ProfilePage;
